import React, { useState, useEffect, useRef } from "react";
import {
  IconButton, Box, Typography, Card, CardContent, Button,
  Chip, CircularProgress, useTheme, TextField, Divider, Popover,
} from "@mui/material";
import {
  Close as CloseIcon, AccessTime as TimeIcon, Add as AddIcon,
  Remove as RemoveIcon, ArrowBack as BackIcon,
  QrCode as QrCodeIcon, ViewList as BarcodeIcon, Image as ImageIcon,
} from "@mui/icons-material";
import InfoIcon from "src/@core/components/icons/InfoIcon";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";
import StockLogo from "src/@core/components/icons/StockLogo";
import { styled } from "@mui/material/styles";
import { IMAGE_URL, ApiEndPoints } from "src/network/endpoints";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';
import 'swiper/css';
import 'swiper/css/pagination';
import { axiosInstance } from "src/network/adapter";
import toast from "react-hot-toast";
import currencyData from "src/constants/currencyData.json";

// Styled Components - Following MenuDialog design patterns
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  zIndex: 10000, // Higher than menu button z-index (9997)
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw", maxHeight: "95vh",
  width: "900px", height: "700px",
  position: "fixed", overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "75vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute", 
  bottom: 0, 
  right: 0,
  width: 25, 
  height: 25, 
  cursor: "se-resize",
  background: "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)",
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""', 
    position: "absolute", 
    bottom: 2, 
    right: 2,
    width: 15, 
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`,
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "16px 24px", background: "white", position: "relative", zIndex: 10,
  cursor: "move", userSelect: "none",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute", left: "50%", top: 8, transform: "translateX(-50%)",
  width: 40, height: 4, backgroundColor: "#E2E8F0", borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white", borderRadius: 16,
  height: "calc(100% - 100px)", overflow: "hidden",
  display: "flex", flexDirection: "column", padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  position: "relative", height: 220, borderRadius: "12px",
  overflow: "hidden", marginBottom: 16,
  backgroundColor: "#F8FAFC",
  [theme.breakpoints.down("sm")]: { 
    height: 160,
    borderRadius: "8px",
    marginBottom: 12,
  },
  [theme.breakpoints.down(400)]: { 
    height: 140,
    borderRadius: "6px",
    marginBottom: 10,
  },
}));

const SwiperContainer = styled(Box)({
  width: "100%", height: "100%", position: "relative",
  "& .swiper": {
    width: "100%", height: "100%",
  },
  "& .swiper-slide": {
    display: "flex", alignItems: "center", justifyContent: "center",
    backgroundColor: "#F8FAFC",
  },
  "& .swiper-pagination": {
    bottom: 8,
  },
  "& .swiper-pagination-bullet": {
    width: 8, height: 8, backgroundColor: "#94A3B8",
    "&.swiper-pagination-bullet-active": {
      backgroundColor: "#6200EE",
    },
  },
});

const FlipButton = styled(IconButton)(({ theme }) => ({
  position: "absolute", top: 8, left: 8, zIndex: 10,
  width: 36, height: 36, backgroundColor: "rgba(255,255,255,0.9)",
  backdropFilter: "blur(4px)", borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,1)",
    transform: "scale(1.05)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    width: 28,
    height: 28,
    top: 6,
    left: 6,
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  [theme.breakpoints.down(400)]: {
    width: 24,
    height: 24,
    top: 4,
    left: 4,
    "& .MuiSvgIcon-root": {
      fontSize: "0.9rem",
    },
  },
}));

const ItemImage = styled("img")({
  width: "100%", height: "100%", objectFit: "cover",
});

const QRCodeContainer = styled(Box)(({ theme }) => ({
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  width: "100%", height: "100%", backgroundColor: "white", borderRadius: 8,
  "& > svg": {
    maxWidth: "120px", maxHeight: "200px", width: "auto", height: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    "& > svg": {
      maxWidth: "100px", maxHeight: "160px",
    },
  },
  [theme.breakpoints.down(400)]: {
    "& > svg": {
      maxWidth: "80px", maxHeight: "120px",
    },
  },
}));

const BarcodeContainer = styled(Box)({
  display: "flex", 
  flexDirection: "column", 
  alignItems: "center", 
  justifyContent: "center",
  width: "100%", 
  height: "100%",
});

const ItemDetailsSection = styled(Box)({
  paddingTop: "16px",
});

// Shared transition and hover effects
const commonTransition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
const primaryColor = "#6200EE";
const primaryLight = "#F2E9FF";

const VariantCard = styled(Card)(({ theme }) => ({
  marginBottom: 16, borderRadius: 16,
  boxShadow: "0 4px 20px -4px rgba(0,0,0,0.1)",
  transition: commonTransition,
  border: "1px solid #F1F5F9", overflow: "hidden",
  "&:hover": {
    boxShadow: "0 12px 40px -8px rgba(0,0,0,0.15)",
    transform: "translateY(-2px)", borderColor: "#E2E8F0",
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: 12,
    borderRadius: 12,
    "&:hover": {
      transform: "translateY(-1px)",
    },
  },
  [theme.breakpoints.down(400)]: {
    marginBottom: 10,
    borderRadius: 10,
  },
}));

const QuantityControls = styled(Box)(({ theme }) => ({
  display: "flex", alignItems: "center", gap: 12,
  marginTop: 12,
  [theme.breakpoints.down("sm")]: { 
    gap: 8,
    marginTop: 10,
  },
  [theme.breakpoints.down(400)]: { 
    gap: 6,
    marginTop: 8,
  },
}));

const QuantityButton = styled(Button)(({ theme }) => ({
  minWidth: '0px !important',
  borderRadius: "50%",
  width: 40,
  height: 40,
  minHeight: 40,
  maxWidth: 40,
  maxHeight: 40,
  background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
  color: "white",
  boxShadow: "0 4px 12px -2px rgba(98, 0, 238, 0.4)",
  transition: commonTransition,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 8px 20px -4px rgba(98, 0, 238, 0.6)",
  },
  "&:disabled": {
    background: "#E2E8F0",
    color: "#94A3B8",
    boxShadow: "none",
    transform: "none",
  },
  [theme.breakpoints.down("sm")]: {
    width: 32,
    height: 32,
    minHeight: 32,
    maxWidth: 32,
    maxHeight: 32,
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  [theme.breakpoints.down(400)]: {
    width: 28,
    height: 28,
    minHeight: 28,
    maxWidth: 28,
    maxHeight: 28,
    "& .MuiSvgIcon-root": {
      fontSize: "0.9rem",
    },
  },
}));

const QuantityDisplay = styled(Box)(({ theme }) => ({
  minWidth: 50, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
  background: "white", border: "2px solid #E2E8F0", borderRadius: 8,
  fontSize: "1rem", fontWeight: 700, color: "#1E293B",
  [theme.breakpoints.down("sm")]: {
    minWidth: 40,
    height: 28,
    fontSize: "0.9rem",
    borderRadius: 6,
  },
  [theme.breakpoints.down(400)]: {
    minWidth: 36,
    height: 24,
    fontSize: "0.8rem",
    borderRadius: 4,
  },
}));

const CustomizeButton = styled(Button)(({ theme }) => ({
  borderRadius: 12, textTransform: "lowercase",
  background: primaryColor, color: "#fff", fontWeight: 600,
  "&:hover": {
    backgroundColor: primaryColor, color: "white",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px -8px rgba(98, 0, 238, 0.4)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: 8,
    fontSize: "0.8rem",
  },
  [theme.breakpoints.down(400)]: {
    borderRadius: 8,
    fontSize: "0.7rem",
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1, padding: "12px 0", overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1", borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 0",
  },
  [theme.breakpoints.down(400)]: {
    padding: "6px 0",
  },
}));

const BottomActions = styled(Box)(({ theme }) => ({
  display: "flex", gap: 16, paddingTop: "16px",
  borderTop: "2px solid #E2E8F0", marginTop: "auto",
  [theme.breakpoints.down("sm")]: {
    gap: 12,
    paddingTop: "12px",
  },
  [theme.breakpoints.down(400)]: {
    gap: 8,
    paddingTop: "10px",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1, height: 56, borderRadius: 16, textTransform: "lowercase",
  fontSize: "1rem", fontWeight: 700, transition: commonTransition,
  "&.primary": {
    background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
    color: "white",
    boxShadow: "0 8px 25px -8px rgba(98, 0, 238, 0.4)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 35px -8px rgba(98, 0, 238, 0.6)",
    },
  },
  "&.secondary": {
    background: primaryLight, color: primaryColor,
    border: "2px solid #E2E8F0",
    "&:hover": {
      backgroundColor: primaryColor, color: "white",
      transform: "translateY(-2px)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    height: 48,
    borderRadius: 12,
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down(400)]: {
    height: 44,
    borderRadius: 10,
    fontSize: "0.8rem",
  },
}));

// Customize Dialog Styled Components
const VariantTab = styled(Button)(({ theme }) => ({
  minWidth: 80, height: 40, borderRadius: 16, textTransform: "lowercase",
  fontSize: "0.9rem", fontWeight: 600, marginRight: 8,
  backgroundColor: "#E4E4E4", color: "#64748B", border: "1px solid #E2E8F0",
  transition: commonTransition,
  "&.selected": {
    backgroundColor: "#FDFDFD", color: "#000000",
    borderColor: "#E2E8F0",
  },
  "&:hover": {
    backgroundColor: "#E2E8F0",
    "&.selected": {
      backgroundColor: "#FDFDFD",
    },
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 70,
    height: 36,
    fontSize: "0.8rem",
    borderRadius: 12,
    marginRight: 6,
  },
  [theme.breakpoints.down(400)]: {
    minWidth: 60,
    height: 32,
    fontSize: "0.7rem",
    borderRadius: 10,
    marginRight: 4,
  },
}));

const IngredientItem = styled(Box)(({ theme }) => ({
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 16px", backgroundColor: "white", borderRadius: 8,
  border: "1px solid #E2E8F0", marginBottom: 8,
  transition: commonTransition,
  "&:hover": {
    borderColor: primaryColor,
    boxShadow: "0 2px 8px rgba(98, 0, 238, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 12px",
    borderRadius: 6,
    marginBottom: 6,
  },
  [theme.breakpoints.down(400)]: {
    padding: "8px 10px",
    borderRadius: 4,
    marginBottom: 4,
  },
}));

const SubButton = styled(Button)(({ theme }) => ({
  minWidth: 60, height: 32, borderRadius: 6, textTransform: "lowercase",
  fontSize: "0.8rem", fontWeight: 600, marginLeft: 8,
  backgroundColor: primaryLight, color: primaryColor,
  border: "1px solid #E2E8F0",
  "&:hover": {
    backgroundColor: primaryColor, color: "white",
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 50,
    height: 28,
    fontSize: "0.7rem",
    borderRadius: 4,
    marginLeft: 6,
  },
  [theme.breakpoints.down(400)]: {
    minWidth: 45,
    height: 24,
    fontSize: "0.6rem",
    borderRadius: 3,
    marginLeft: 4,
  },
}));

const RemoveButton = styled(Button)(({ theme }) => ({
  minWidth: 70, height: 32, borderRadius: 6, textTransform: "lowercase",
  fontSize: "0.8rem", fontWeight: 600, marginLeft: 8,
  backgroundColor: "#FEF2F2", color: "#DC2626",
  border: "1px solid #FECACA",
  "&:hover": {
    backgroundColor: "#DC2626", color: "white",
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 60,
    height: 28,
    fontSize: "0.7rem",
    borderRadius: 4,
    marginLeft: 6,
  },
  [theme.breakpoints.down(400)]: {
    minWidth: 55,
    height: 24,
    fontSize: "0.6rem",
    borderRadius: 3,
    marginLeft: 4,
  },
}));

// API call functions
const addToCart = async (cartData, conversationId, locationId) => {
  try {
    const url = locationId 
      ? `${ApiEndPoints.CART.add}/${conversationId}?locationId=${locationId}`
      : `${ApiEndPoints.CART.add}/${conversationId}`;
    const response = await axiosInstance.post(url, cartData);
    return response.data;
  } catch (error) {
    
    throw error;
  }
};

const deleteCartItem = async (conversationId, menuItemId, locationId) => {
  try {
    const url = locationId 
      ? `${ApiEndPoints.CART.delete}/${conversationId}/${menuItemId}?locationId=${locationId}`
      : `${ApiEndPoints.CART.delete}/${conversationId}/${menuItemId}`;
    const response = await axiosInstance.delete(url);
    return response.data;
  } catch (error) {
    
    throw error;
  }
};

const ItemOrderDetailDialog = ({ 
  open, 
  onClose, 
  onBack, 
  itemData, 
  cartItemData,
  businessLocationId,
  businessId,
  conversationId 
}) => {
  const theme = useTheme();
  const dialogRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // console.log("businessLocationId------>", businessLocationId);
  // State for variants and quantities
  const [selectedVariants, setSelectedVariants] = useState({});
  const [variantNotes, setVariantNotes] = useState({});
  
  // State for image view mode (image, qrcode, barcode)
  const [viewMode, setViewMode] = useState('image');
  
  // State for customize dialog
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  const [selectedVariantForCustomize, setSelectedVariantForCustomize] = useState(null);
  const [showSubstitutionOptions, setShowSubstitutionOptions] = useState(false);
  const [selectedIngredientForSub, setSelectedIngredientForSub] = useState(null);
  const [customizations, setCustomizations] = useState({});
  
  // State for add sides dialog
  const [showAddSidesDialog, setShowAddSidesDialog] = useState(false);
  const [selectedSides, setSelectedSides] = useState({});
  const [addedSides, setAddedSides] = useState({}); // This will store sides per individual item
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomizeLoading, setIsCustomizeLoading] = useState(false);
  const [isSidesLoading, setIsSidesLoading] = useState(false);
  
  // Draft state
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  
  // Popover state
  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);
  const [stockPopoverAnchor, setStockPopoverAnchor] = useState(null);
  const [stockPopoverData, setStockPopoverData] = useState(null);

  // Initialize quantities when item data changes
  useEffect(() => {
    if (itemData && open) {
      setIsLoading(true);
      
      // Simulate loading time for better UX
      setTimeout(() => {
        const initialQuantities = {};
        const initialNotes = {};
        const initialCustomizations = {};
        const initialAddedSides = {};
        
        if (cartItemData) {
          // Pre-populate with existing cart data
          if (cartItemData.variantEnabled && cartItemData.variants?.length > 0) {
            // Handle variants from cart
            cartItemData.variants.forEach((variant, variantIndex) => {
              const variantQuantity = variant.items?.length || 0;
              initialQuantities[variantIndex] = variantQuantity;
              initialNotes[variantIndex] = variant.note || "";
              
              // Process each individual item's customizations
              variant.items?.forEach((item, itemIndex) => {
                const tabKey = `${variantIndex}-${itemIndex + 1}`;
                
                // Process ingredient modifications
                const ingredientModifications = {};
                const substitutions = {};
                const removed = {};
                
                item.customizations?.ingredientModifications?.forEach(mod => {
                  if (mod.action === 'remove') {
                    removed[mod.ingredientName] = true;
                  } else if (mod.action === 'substitute') {
                    substitutions[mod.ingredientName] = mod.substituteWith;
                  }
                });
                
                initialCustomizations[tabKey] = {
                  removed,
                  substitutions
                };
                
                // Process sides
                const itemSides = {};
                item.customizations?.sides?.forEach(side => {
                  itemSides[side.sideItemId] = side.quantity;
                });
                initialAddedSides[tabKey] = itemSides;
              });
            });
          } else {
            // Handle single item from cart - NEW STRUCTURE
            initialQuantities['single'] = cartItemData.quantity || 0;
            initialNotes['single'] = cartItemData.note || "";
            
            // Check if cart data has new structure with items array
            if (cartItemData.items && cartItemData.items.length > 0) {
              // NEW STRUCTURE: Process individual items from items array
              cartItemData.items.forEach((item, itemIndex) => {
                const tabKey = `single-${item.itemNumber}`;
                
                // Process ingredient modifications for this specific item
                const ingredientModifications = {};
                const substitutions = {};
                const removed = {};
                
                item.customizations?.ingredientModifications?.forEach(mod => {
                  if (mod.action === 'remove') {
                    removed[mod.ingredientName] = true;
                  } else if (mod.action === 'substitute') {
                    substitutions[mod.ingredientName] = mod.substituteWith;
                  }
                });
                
                initialCustomizations[tabKey] = {
                  removed,
                  substitutions
                };
                
                // Process sides for this specific item
                const itemSides = {};
                item.customizations?.sides?.forEach(side => {
                  itemSides[side.sideItemId] = side.quantity;
                });
                initialAddedSides[tabKey] = itemSides;
              });
            } else {
              // OLD STRUCTURE: Fallback for backward compatibility
              const ingredientModifications = {};
              const substitutions = {};
              const removed = {};
              
              cartItemData.customizations?.ingredientModifications?.forEach(mod => {
                if (mod.action === 'remove') {
                  removed[mod.ingredientName] = true;
                } else if (mod.action === 'substitute') {
                  substitutions[mod.ingredientName] = mod.substituteWith;
                }
              });
              
              // Create individual tabs for each quantity of single item
              for (let i = 1; i <= (cartItemData.quantity || 0); i++) {
                const tabKey = `single-${i}`;
                initialCustomizations[tabKey] = {
                  removed: { ...removed },
                  substitutions: { ...substitutions }
                };
                
                // Process sides for single item
                const itemSides = {};
                cartItemData.customizations?.sides?.forEach(side => {
                  itemSides[side.sideItemId] = side.quantity;
                });
                initialAddedSides[tabKey] = itemSides;
              }
            }
          }
        } else {
          // Initialize empty state for new item
          if (itemData.variantEnabled && itemData.variants?.length > 0) {
            // Initialize all variants with quantity 0
            itemData.variants.forEach((variant, index) => {
              initialQuantities[index] = 0;
            });
          } else {
            // Single item without variants
            initialQuantities['single'] = 0;
          }
        }
        
        // Cap quantities to available stock if they exceed stock limits
        if (businessLocationId) {
          if (itemData.variantEnabled && itemData.variants?.length > 0) {
            itemData.variants.forEach((variant, index) => {
              if (initialQuantities[index] !== undefined && variant.perLocationStock?.length > 0) {
                const locationStock = variant.perLocationStock.find(
                  stock => stock.locationId === businessLocationId
                );
                const stockOnHand = locationStock?.stockOnHand;
                if (stockOnHand !== null && stockOnHand !== undefined && initialQuantities[index] > stockOnHand) {
                  initialQuantities[index] = stockOnHand;
                  toast.error(`quantity capped to available stock: ${stockOnHand}`);
                }
              }
            });
          } else {
            if (initialQuantities['single'] !== undefined && itemData.locationStock?.length > 0) {
              const locationStock = itemData.locationStock.find(
                stock => stock.locationId === businessLocationId
              );
              const stockOnHand = locationStock?.stockOnHand;
              if (stockOnHand !== null && stockOnHand !== undefined && initialQuantities['single'] > stockOnHand) {
                initialQuantities['single'] = stockOnHand;
                toast.error(`quantity capped to available stock: ${stockOnHand}`);
              }
            }
          }
        }
        
        setSelectedVariants(initialQuantities);
        setVariantNotes(initialNotes);
        setCustomizations(initialCustomizations);
        setAddedSides(initialAddedSides);
        setViewMode('image'); // Reset to image view when dialog opens
        setShowCustomizeDialog(false);
        setSelectedVariantForCustomize(null);
        setShowSubstitutionOptions(false);
        setSelectedIngredientForSub(null);
        setShowAddSidesDialog(false);
        setSelectedSides({});
        setIsLoading(false);
      }, 500);
    }
  }, [itemData, cartItemData, open]);

  // Drag and resize functionality (same as MenuDialog)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY))
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;
        
        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Initialize dialog when opened
  useEffect(() => {
    if (open) {
      // Center the dialog on screen with responsive dimensions
      const isMobile = window.innerWidth < 960; // md breakpoint
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 900;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 700;
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;
      
      setDialogPosition({ 
        x: Math.max(0, centerX),
        y: Math.max(0, centerY) 
      });
    }
  }, [open]);

  // Event handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Info popover handlers
  const handleInfoPopoverOpen = (event) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoPopoverClose = () => {
    setInfoPopoverAnchor(null);
  };

  // Stock popover handlers
  const handleStockPopoverOpen = (event, stockData) => {
    setStockPopoverAnchor(event.currentTarget);
    setStockPopoverData(stockData);
  };

  const handleStockPopoverClose = () => {
    setStockPopoverAnchor(null);
    setStockPopoverData(null);
  };

  // Reusable nutrition row component
  const NutritionRow = ({ label, value, unit = "" }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ 
        color: "#64748B", 
        fontSize: { xs: "0.8rem", sm: "0.85rem" }, 
        textTransform: "lowercase",
        flex: 1
      }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ 
        fontWeight: 600, 
        color: "#1E293B", 
        fontSize: { xs: "0.8rem", sm: "0.85rem" },
        ml: 1
      }}>
        {value}{unit}
      </Typography>
    </Box>
  );

  // Get nutritional info helper
  const getNutritionData = () => {
    if (itemData.variantEnabled && itemData.variants?.length > 0) {
      return itemData.variants[0]?.nutrition;
    }
    return itemData.nutrition;
  };

  // Get stock information for a specific variant
  const getVariantStockInformation = (variant) => {
    if (!itemData?.showStockToCustomer || !variant?.perLocationStock?.length > 0) return null;
    
    const locationStock = businessLocationId
      ? variant.perLocationStock.find(stock => stock.locationId === businessLocationId)
      : variant.perLocationStock[0];
    
    if (!locationStock) return null;
    
    return {
      stockOnHand: locationStock.stockOnHand ?? 0,
      stockCommitted: locationStock.stockCommitted ?? 0,
      awaitingNewStockDelivery: locationStock.awaitingNewStockDelivery || null,
      awaitingNewStockCount: locationStock.awaitingNewStockCount ?? 0,
    };
  };

  // Get stock information for single item
  const getSingleItemStockInformation = () => {
    if (!itemData?.showStockToCustomer || !itemData.locationStock?.length > 0) return null;
    
    const locationStock = businessLocationId
      ? itemData.locationStock.find(stock => stock.locationId === businessLocationId)
      : itemData.locationStock[0];
    
    if (!locationStock) return null;
    
    return {
      stockOnHand: locationStock.stockOnHand ?? 0,
      stockCommitted: locationStock.stockCommitted ?? 0,
      awaitingNewStockDelivery: locationStock.awaitingNewStockDelivery || null,
      awaitingNewStockCount: locationStock.awaitingNewStockCount ?? 0,
    };
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Draft save functionality - automatically save when closing dialog with items
  // This prevents users from losing their customizations when they accidentally close the dialog
  // The item is saved with isDraft: true flag and all customizations are preserved
  const saveAsDraft = async () => {
    try {
      const totalItems = getTotalItems();
      if (totalItems > 0) {
        const cartPayload = generateCartPayload();        
        // Add draft flag to the payload
        const draftPayload = {
          ...cartPayload,
          isDraft: true
        };
        
        await addToCart(draftPayload, conversationId, businessLocationId);
        setIsDraftSaved(true);        
        // Show success message
        toast.success("item saved as draft");
      }
    } catch (error) {
      
      toast.error("failed to save as draft");
    }
  };

  // Enhanced close handler that saves as draft
  const handleClose = async () => {
    await saveAsDraft();
    onClose();
  };

  // Enhanced back handler that saves as draft
  const handleBack = async () => {
    await saveAsDraft();
    onBack();
  };

  // Quantity management
  const updateQuantity = (variantKey, change) => {
    const currentQuantity = selectedVariants[variantKey] || 0;
    
    // Check stock limit when increasing quantity
    if (change > 0) {
      let stockOnHand = null;
      
      if (variantKey === 'single') {
        // For single item without variants
        stockOnHand = getItemStockOnHand(itemData);
      } else {
        // For variant-enabled items
        const variant = itemData.variants?.[variantKey];
        if (variant) {
          stockOnHand = getVariantStockOnHand(variant);
        }
      }
      
      // If stock is limited and we've reached the limit, prevent increase
      if (stockOnHand !== null && currentQuantity >= stockOnHand) {
        toast.error(`only ${stockOnHand} items available in stock`);
        return;
      }
      
      // If stock is limited, cap the new quantity at stock limit
      if (stockOnHand !== null) {
        const newQuantity = Math.min(currentQuantity + change, stockOnHand);
        setSelectedVariants(prev => ({
          ...prev,
          [variantKey]: newQuantity
        }));
        return;
      }
    }
    
    // If reducing quantity and there are customized items for this variant
    if (change < 0 && currentQuantity > 0) {
      // Check if there are any customized items for this variant
      const hasCustomizedItems = Object.keys(customizations).some(tabKey => {
        if (variantKey === 'single') {
          return tabKey.startsWith('single-');
        } else {
          return tabKey.startsWith(`${variantKey}-`);
        }
      });
      
      // If there are customized items, open customize dialog to let user choose which to remove
      if (hasCustomizedItems) {
        handleOpenCustomize(variantKey);
        return; // Don't update quantity yet, let user choose which item to remove
      }
    }
    
    // Update quantity normally (for decrease or when no stock limit)
    const newQuantity = Math.max(0, currentQuantity + change);
    setSelectedVariants(prev => ({
      ...prev,
      [variantKey]: newQuantity
    }));
  };

  // Note management for variants
  const updateVariantNote = (variantKey, note) => {
    setVariantNotes(prev => ({
      ...prev,
      [variantKey]: note
    }));
  };

  // Flip between image, qrcode, and barcode
  const handleFlip = () => {
    if (viewMode === 'image') {
      setViewMode('qrcode');
    } else if (viewMode === 'qrcode') {
      setViewMode('barcode');
    } else {
      setViewMode('image');
    }
  };

  // Get flip button icon based on current view mode
  const getFlipIcon = () => {
    if (viewMode === 'image') {
      return <QrCodeIcon sx={{ fontSize: 18, color: "#6200EE" }} />;
    } else if (viewMode === 'qrcode') {
      return <BarcodeIcon sx={{ fontSize: 18, color: "#6200EE" }} />;
    } else {
      return <ImageIcon sx={{ fontSize: 18, color: "#6200EE" }} />;
    }
  };

  // Customize dialog handlers
  const handleOpenCustomize = (variantKey) => {
    setShowCustomizeDialog(true);
    setIsCustomizeLoading(true);
    // Set the first available tab as default after dialog opens
    setTimeout(() => {
      const tabs = getVariantsForTabs();
      if (tabs.length > 0) {
        setSelectedVariantForCustomize(tabs[0].key);
      }
      setIsCustomizeLoading(false);
    }, 300);
  };

  const handleCloseCustomize = () => {
    setShowCustomizeDialog(false);
    setSelectedVariantForCustomize(null);
    setShowSubstitutionOptions(false);
    setSelectedIngredientForSub(null);
  };

  const handleBackFromCustomize = () => {
    if (showSubstitutionOptions) {
      setShowSubstitutionOptions(false);
      setSelectedIngredientForSub(null);
    } else {
      handleCloseCustomize();
    }
  };

  const handleShowSubstitutionOptions = (ingredient) => {
    setSelectedIngredientForSub(ingredient);
    setShowSubstitutionOptions(true);
  };

  const handleSubstituteIngredient = (originalIngredient, substitute) => {
    const tabKey = selectedVariantForCustomize;
    
    // If they select the original ingredient name, clear the substitution
    if (substitute === originalIngredient.name) {
      setCustomizations(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          substitutions: {
            ...prev[tabKey]?.substitutions,
            [originalIngredient.name]: null
          }
        }
      }));
    } else {
      // Set the substitution
      setCustomizations(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          substitutions: {
            ...prev[tabKey]?.substitutions,
            [originalIngredient.name]: substitute
          }
        }
      }));
    }
    
    setShowSubstitutionOptions(false);
    setSelectedIngredientForSub(null);
  };

  const handleRemoveIngredient = (ingredient) => {
    const tabKey = selectedVariantForCustomize;
    setCustomizations(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        removed: {
          ...prev[tabKey]?.removed,
          [ingredient.name]: true
        }
      }
    }));
  };

  // Remove specific variant from quantity
  const handleRemoveVariant = (tabKey) => {
    let variantKey;
    let newQuantity;
    
    if (tabKey.startsWith('single-')) {
      // For single item, decrease quantity by 1
      variantKey = 'single';
      newQuantity = Math.max(0, (selectedVariants['single'] || 0) - 1);
      setSelectedVariants(prev => ({
        ...prev,
        'single': newQuantity
      }));
    } else {
      // For variants, parse the variant index and decrease quantity
      const variantIndex = parseInt(tabKey.split('-')[0]);
      variantKey = variantIndex;
      newQuantity = Math.max(0, (selectedVariants[variantIndex] || 0) - 1);
      setSelectedVariants(prev => ({
        ...prev,
        [variantIndex]: newQuantity
      }));
    }
    
    // Remove customizations for this specific item
    setCustomizations(prev => {
      const { [tabKey]: removed, ...rest } = prev;
      return rest;
    });
    
    // Remove sides for this specific item
    setAddedSides(prev => {
      const { [tabKey]: removed, ...rest } = prev;
      return rest;
    });
  };

  // Add sides dialog handlers
  const handleOpenAddSides = () => {
    setShowAddSidesDialog(true);
    setIsSidesLoading(true);
    // Initialize selected sides with current added sides for this specific item
    setTimeout(() => {
      const currentItemSides = addedSides[selectedVariantForCustomize] || {};
      setSelectedSides({ ...currentItemSides });
      setIsSidesLoading(false);
    }, 200);
  };

  const handleCloseAddSides = () => {
    setShowAddSidesDialog(false);
    setSelectedSides({});
  };

  const handleAddSides = () => {
    // Store sides for this specific item (tab key)
    setAddedSides(prev => ({
      ...prev,
      [selectedVariantForCustomize]: { ...selectedSides }
    }));
    setShowAddSidesDialog(false);
    setSelectedSides({});
  };

  const updateSideQuantity = (sideId, change) => {
    setSelectedSides(prev => {
      const currentQty = prev[sideId] || 0;
      const newQty = Math.max(0, currentQty + change);
      
      if (newQty === 0) {
        const { [sideId]: removed, ...rest } = prev;
        return rest;
      }
      
      return {
        ...prev,
        [sideId]: newQty
      };
    });
  };

  const removeAddedSide = (sideId) => {
    setAddedSides(prev => {
      const currentItemSides = prev[selectedVariantForCustomize] || {};
      const { [sideId]: removed, ...rest } = currentItemSides;
      return {
        ...prev,
        [selectedVariantForCustomize]: rest
      };
    });
  };



  // Utility functions
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "$";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : "$";
  };

  const formatPrice = (price, currencyCode = "USD") => {
    if (price === null || price === undefined) {
      return "price not available";
    }
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${price}`;
  };
  
  const formatTime = (minutes) => `${minutes} mins`;
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return imagePath.startsWith('http') ? imagePath : `${IMAGE_URL}${imagePath}`;
  };

  // Get price and currency for a menu item based on location
  const getItemPriceAndCurrency = (item) => {
    if (!item) return { price: null, currency: "USD" };

    // For variant-enabled items
    if (item.variantEnabled && item.variants?.length > 0) {
      const firstVariant = item.variants[0];
      
      // Try to find price for current location
      if (businessLocationId && firstVariant.perLocationStock?.length > 0) {
        const locationStock = firstVariant.perLocationStock.find(
          (stock) => stock.locationId === businessLocationId
        );
        
        if (locationStock?.price !== undefined && locationStock?.currency) {
          return {
            price: locationStock.price,
            currency: locationStock.currency,
          };
        }
      }
      
      // Fallback to first available location stock
      if (firstVariant.perLocationStock?.length > 0) {
        const firstStock = firstVariant.perLocationStock[0];
        if (firstStock?.price !== undefined && firstStock?.currency) {
          return {
            price: firstStock.price,
            currency: firstStock.currency,
          };
        }
      }
    }
    
    // For non-variant items
    // Try to find price for current location
    if (businessLocationId && item.locationStock?.length > 0) {
      const locationStock = item.locationStock.find(
        (stock) => stock.locationId === businessLocationId
      );
      
      if (locationStock?.price !== undefined && locationStock?.currency) {
        return {
          price: locationStock.price,
          currency: locationStock.currency,
        };
      }
    }
    
    // Fallback to first available location stock
    if (item.locationStock?.length > 0) {
      const firstStock = item.locationStock[0];
      if (firstStock?.price !== undefined && firstStock?.currency) {
        return {
          price: firstStock.price,
          currency: firstStock.currency,
        };
      }
    }
    
    // Final fallback
    return {
      price: item.price || null,
      currency: item.currency || "USD",
    };
  };

  // Helper function to check if variant has stock for current location
  const hasVariantStockForLocation = (variant) => {
    if (!businessLocationId) return true; // Show all if no location specified
    if (!variant.perLocationStock?.length > 0) return false;
    return variant.perLocationStock.some(
      stock => stock.locationId === businessLocationId
    );
  };

  // Get available stock for a variant at current location
  const getVariantStockOnHand = (variant) => {
    if (!businessLocationId || !variant?.perLocationStock?.length > 0) return null;
    const locationStock = variant.perLocationStock.find(
      stock => stock.locationId === businessLocationId
    );
    return locationStock?.stockOnHand ?? null;
  };

  // Get available stock for a single item at current location
  const getItemStockOnHand = (item) => {
    if (!businessLocationId || !item?.locationStock?.length > 0) return null;
    const locationStock = item.locationStock.find(
      stock => stock.locationId === businessLocationId
    );
    return locationStock?.stockOnHand ?? null;
  };

  // Get variant price and currency based on location
  const getVariantPriceAndCurrency = (variant, variantIndex) => {
    if (!variant) return { price: null, currency: "USD" };
    
    // Try to find price for current location
    if (businessLocationId && variant.perLocationStock?.length > 0) {
      const locationStock = variant.perLocationStock.find(
        (stock) => stock.locationId === businessLocationId
      );
      
      if (locationStock?.price !== undefined && locationStock?.currency) {
        return {
          price: locationStock.price,
          currency: locationStock.currency,
        };
      }
    }
    
    // Fallback to first available location stock
    if (variant.perLocationStock?.length > 0) {
      const firstStock = variant.perLocationStock[0];
      if (firstStock?.price !== undefined && firstStock?.currency) {
        return {
          price: firstStock.price,
          currency: firstStock.currency,
        };
      }
    }
    
    // Final fallback
    return {
      price: variant.price || null,
      currency: variant.currency || itemData?.currency || "USD",
    };
  };

  // Calculate total items in cart
  const getTotalItems = () => {
    return Object.values(selectedVariants).reduce((sum, qty) => sum + qty, 0);
  };

  // Generate cart payload for API
  const generateCartPayload = () => {
    if (!itemData) return null;

    const cartItem = {
      menuItemId: itemData._id,
      businessId: businessId, // Use the actual business ID from menu data
      locationId: businessLocationId, // Use the actual location ID
      variantEnabled: itemData.variantEnabled || false,
    };

    if (itemData.variantEnabled && itemData.variants?.length > 0) {
      // Handle variants
      cartItem.variants = [];
      
      itemData.variants.forEach((variant, variantIndex) => {
        const quantity = selectedVariants[variantIndex] || 0;
        if (quantity > 0) {
          const variantData = {
            variantName: variant.name,
            note: variantNotes[variantIndex] || "",
            items: []
          };

          // Create individual items for each quantity
          for (let i = 1; i <= quantity; i++) {
            const tabKey = `${variantIndex}-${i}`;
            const itemCustomizations = customizations[tabKey] || {};
            const itemSides = addedSides[tabKey] || {};

            const item = {
              variantId: variant._id,
              quantity: 1, // Each item is quantity 1
              variantPrice: getVariantPrice(variantIndex), // Show variant price
              totalPrice: getVariantTotalPrice(variantIndex, tabKey), // Add total price calculation
              customizations: {
                ingredientModifications: [],
                sides: []
              }
            };

            // Add ALL ingredients with their modification status
            const allIngredients = itemData.ingredients || [];
            
            allIngredients.forEach(ingredient => {
              const ingredientName = ingredient.name;
              const isRemoved = itemCustomizations.removed?.[ingredientName];
              const substitution = itemCustomizations.substitutions?.[ingredientName];
              
              if (isRemoved) {
                // Ingredient was removed
                item.customizations.ingredientModifications.push({
                  ingredientName: ingredientName,
                  action: "remove"
                });
              } else if (substitution && substitution !== ingredientName) {
                // Ingredient was substituted
                item.customizations.ingredientModifications.push({
                  ingredientName: ingredientName,
                  action: "substitute",
                  substituteWith: substitution
                });
              } else {
                // Ingredient has no modification
                item.customizations.ingredientModifications.push({
                  ingredientName: ingredientName,
                  action: "no_modification"
                });
              }
            });

            // Add sides
            Object.entries(itemSides).forEach(([sideId, sideQuantity]) => {
              const side = getSideData(sideId);
              if (side) {
                item.customizations.sides.push({
                  sideItemId: sideId,
                  sideName: side.name,
                  quantity: sideQuantity,
                  price: getSidePrice(side)
                });
              }
            });

            variantData.items.push(item);
          }

          cartItem.variants.push(variantData);
        }
      });
    } else {
      // Handle single item without variants - NEW STRUCTURE
      const quantity = selectedVariants['single'] || 0;
      if (quantity > 0) {
        cartItem.quantity = quantity;
        cartItem.note = variantNotes['single'] || "";
        
        // Calculate total price for all quantities of single item
        const singleItemPrice = getVariantPrice('single');
        const totalSidesPrice = getTotalSidesPrice(); // Get total sides price for all single items
        cartItem.itemPrice = singleItemPrice * quantity; // Show item price based on quantity
        cartItem.totalPrice = (singleItemPrice * quantity) + totalSidesPrice;
        
        // Create individual items array for each quantity
        cartItem.items = [];
        
        // Create individual items for each quantity
        for (let i = 1; i <= quantity; i++) {
          const tabKey = `single-${i}`;
          const itemCustomizations = customizations[tabKey] || {};
          const itemSides = addedSides[tabKey] || {};

          const item = {
            itemNumber: i,
            customizations: {
              ingredientModifications: [],
              sides: []
            }
          };

          // Add ALL ingredients with their modification status for this specific item
          const allIngredients = itemData.ingredients || [];
          
          allIngredients.forEach(ingredient => {
            const ingredientName = ingredient.name;
            const isRemoved = itemCustomizations.removed?.[ingredientName];
            const substitution = itemCustomizations.substitutions?.[ingredientName];
            
            if (isRemoved) {
              // Ingredient was removed
              item.customizations.ingredientModifications.push({
                ingredientName: ingredientName,
                action: "remove"
              });
            } else if (substitution && substitution !== ingredientName) {
              // Ingredient was substituted
              item.customizations.ingredientModifications.push({
                ingredientName: ingredientName,
                action: "substitute",
                substituteWith: substitution
              });
            } else {
              // Ingredient has no modification
              item.customizations.ingredientModifications.push({
                ingredientName: ingredientName,
                action: "no_modification"
              });
            }
          });

          // Add sides for this specific item
          Object.entries(itemSides).forEach(([sideId, sideQuantity]) => {
            const side = getSideData(sideId);
            if (side) {
              item.customizations.sides.push({
                sideItemId: sideId,
                sideName: side.name,
                quantity: sideQuantity,
                price: getSidePrice(side)
              });
            }
          });

          cartItem.items.push(item);
        }
      }
    }

    return cartItem;
  };

  // Calculate customized items count
  const getCustomizedCount = () => {
    return Object.keys(customizations).length;
  };

  // Calculate total sides price for a specific item
  const getTotalSidesPrice = (itemKey = null) => {
    if (itemKey) {
      // For specific item (variant case)
      const sidesToCalculate = addedSides[itemKey] || {};
      return Object.entries(sidesToCalculate).reduce((total, [sideId, quantity]) => {
        const side = itemData.sides?.find(s => s._id === sideId);
        if (!side) return total; // Skip if side not found
        return total + getSidePrice(side) * quantity;
      }, 0);
    } else {
      // For single items without variants, calculate total sides for all single items
      return Object.entries(addedSides || {}).reduce((total, [tabKey, itemSides]) => {
        if (tabKey.startsWith('single-')) {
          return total + Object.entries(itemSides).reduce((itemTotal, [sideId, quantity]) => {
            const side = itemData.sides?.find(s => s._id === sideId);
            if (!side) return itemTotal; // Skip if side not found
            return itemTotal + getSidePrice(side) * quantity;
          }, 0);
        }
        return total;
      }, 0);
    }
  };

  // Get side data by ID
  const getSideData = (sideId) => {
    return itemData.sides?.find(side => side._id === sideId);
  };

  // Get side price - check location stock first, then variants, then direct price
  const getSidePrice = (side) => {
    if (!side) return 0; // Return 0 if side is undefined/null
    
    // First, check for location-based pricing
    if (businessLocationId && side.locationStock?.length > 0) {
      const locationStock = side.locationStock.find(
        stock => stock.locationId === businessLocationId
      );
      if (locationStock?.price !== undefined && locationStock?.price !== null) {
        return locationStock.price;
      }
    }
    
    // Fallback to first available location stock
    if (side.locationStock?.length > 0) {
      const firstStock = side.locationStock[0];
      if (firstStock?.price !== undefined && firstStock?.price !== null) {
        return firstStock.price;
      }
    }
    
    // Check variants if they exist
    if (side.variants && side.variants.length > 0) {
      // Get the lowest price from variants
      const variantPrices = side.variants.map(v => {
        // Check variant's perLocationStock first
        if (businessLocationId && v.perLocationStock?.length > 0) {
          const locationStock = v.perLocationStock.find(
            stock => stock.locationId === businessLocationId
          );
          if (locationStock?.price !== undefined && locationStock?.price !== null) {
            return locationStock.price;
          }
        }
        // Fallback to variant's direct price
        return v.price || 0;
      }).filter(price => price > 0);
      
      if (variantPrices.length > 0) {
        return Math.min(...variantPrices);
      }
    }
    
    // Return direct price if no variants or location stock
    return side.price || 0;
  };

  // Get side name with variant info if applicable
  const getSideDisplayName = (side) => {
    if (!side) return 'Unknown Side'; // Return default name if side is undefined/null
    
    if (side.variants && side.variants.length > 0) {
      const lowestPriceVariant = side.variants.find(v => v.price === getSidePrice(side));
      return `${side.name} (${lowestPriceVariant?.name || 'from'})`;
    }
    return side.name;
  };

  // Get side currency - check location stock first, then variants, then direct currency
  const getSideCurrency = (side) => {
    if (!side) return 'USD'; // Return default currency if side is undefined/null
    
    // First, check for location-based currency
    if (businessLocationId && side.locationStock?.length > 0) {
      const locationStock = side.locationStock.find(
        stock => stock.locationId === businessLocationId
      );
      if (locationStock?.currency) {
        return locationStock.currency;
      }
    }
    
    // Fallback to first available location stock currency
    if (side.locationStock?.length > 0) {
      const firstStock = side.locationStock[0];
      if (firstStock?.currency) {
        return firstStock.currency;
      }
    }
    
    // Check variants if they exist
    if (side.variants && side.variants.length > 0) {
      // Get currency from first variant with location stock
      for (const variant of side.variants) {
        if (businessLocationId && variant.perLocationStock?.length > 0) {
          const locationStock = variant.perLocationStock.find(
            stock => stock.locationId === businessLocationId
          );
          if (locationStock?.currency) {
            return locationStock.currency;
          }
        }
        // Fallback to variant's direct currency
        if (variant.currency) {
          return variant.currency;
        }
      }
    }
    
    // Return direct currency if no variants or location stock
    return side.currency || 'USD';
  };

  // Calculate variant total price (variant price + sides price)
  const getVariantTotalPrice = (variantIndex, itemKey = null) => {
    let variantPrice = 0;
    
    // Get variant price
    if (variantIndex === 'single') {
      const { price } = getItemPriceAndCurrency(itemData);
      variantPrice = price || 0;
    } else {
      const variant = itemData.variants?.[variantIndex];
      const { price } = getVariantPriceAndCurrency(variant, variantIndex);
      variantPrice = price || 0;
    }
    
    // Get sides price for this specific item
    const sidesPrice = getTotalSidesPrice(itemKey);
    
    return variantPrice + sidesPrice;
  };

  // Get variant price only (without sides)
  const getVariantPrice = (variantIndex) => {
    if (variantIndex === 'single') {
      const { price } = getItemPriceAndCurrency(itemData);
      return price || 0;
    } else {
      const variant = itemData.variants?.[variantIndex];
      const { price } = getVariantPriceAndCurrency(variant, variantIndex);
      return price || 0;
    }
  };

  // Get variant data for customize dialog
  const getVariantData = () => {
    if (!selectedVariantForCustomize) {
      return {
        name: itemData.name,
        description: itemData.description,
        ingredients: itemData.ingredients || [],
        removableIngredients: itemData.removableIngredients || [],
        allowIngredientRemoval: itemData.allowIngredientRemoval || false,
        allowFreeSubstitution: itemData.allowFreeSubstitution || false,
      };
    }

    // Parse the selected tab key to get variant info
    if (selectedVariantForCustomize.startsWith('single-')) {
      return {
        name: itemData.name,
        description: itemData.description,
        ingredients: itemData.ingredients || [],
        removableIngredients: itemData.removableIngredients || [],
        allowIngredientRemoval: itemData.allowIngredientRemoval || false,
        allowFreeSubstitution: itemData.allowFreeSubstitution || false,
      };
    } else {
      // Parse variant index from key (e.g., "0-1" -> variantIndex: 0)
      const variantIndex = parseInt(selectedVariantForCustomize.split('-')[0]);
      const variant = itemData.variants?.[variantIndex];
      return {
        name: variant?.name || '',
        description: variant?.description || '',
        ingredients: itemData.ingredients || [],
        removableIngredients: itemData.removableIngredients || [],
        allowIngredientRemoval: itemData.allowIngredientRemoval || false,
        allowFreeSubstitution: itemData.allowFreeSubstitution || false,
      };
    }
  };

  // Get all individual items for tabs (each quantity gets its own tab)
  const getVariantsForTabs = () => {
    const tabs = [];
    
    if (itemData.variantEnabled && itemData.variants?.length > 0) {
      // For each variant, create individual tabs for each quantity
      itemData.variants.forEach((variant, variantIndex) => {
        const quantity = selectedVariants[variantIndex] || 0;
        for (let i = 1; i <= quantity; i++) {
          tabs.push({
            key: `${variantIndex}-${i}`, // Unique key for each individual item
            variantIndex: variantIndex,
            itemNumber: i,
            name: variant.name,
            variant: variant,
          });
        }
      });
    } else {
      // For single item without variants
      const singleQuantity = selectedVariants['single'] || 0;
      for (let i = 1; i <= singleQuantity; i++) {
        tabs.push({
          key: `single-${i}`,
          variantIndex: 'single',
          itemNumber: i,
          name: itemData.name,
          variant: null,
        });
      }
    }
    
    return tabs;
  };

  // Render variant card
  const renderVariantCard = (variant, index) => {
    const quantity = selectedVariants[index] || 0;
    const hasQuantity = quantity > 0;
    const note = variantNotes[index] || "";
    const stockOnHand = getVariantStockOnHand(variant);
    const isStockLimited = stockOnHand !== null;
    const isMaxStockReached = isStockLimited && quantity >= stockOnHand;

    return (
      <Box key={index} sx={{ marginBottom: 2 }}>
        <VariantCard>
          <CardContent sx={{ 
            padding: { xs: "16px", sm: "18px", md: "20px" } 
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.1rem" }, 
                    color: "#1E293B", 
                    textTransform: "lowercase"
                  }}>
                    {variant.name}
                  </Typography>
                  {/* Stock Icon with Popover for variant */}
                  {itemData?.showStockToCustomer && getVariantStockInformation(variant) && (
                    <>
                      <Box
                        onMouseEnter={(e) => handleStockPopoverOpen(e, getVariantStockInformation(variant))}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'color 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: "#E1E0DA",
                          p: 1,
                          borderRadius: 1,
                          '&:hover': {
                            '& svg path': {
                              fill: primaryColor
                            }
                          }
                        }}
                      >
                        <StockLogo 
                          size={14}
                          color="#000"
                        />
                      </Box>
                    </>
                  )}
                </Box>
                <Typography variant="body2" sx={{ 
                  color: "#64748B", 
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.9rem" }, 
                  textTransform: "lowercase", 
                  mb: 0.5 
                }}>
                  {variant.description}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: "#94A3B8", 
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.8rem" }, 
                  fontWeight: 600 
                }}>
                  #{String(index + 1).padStart(2, '0')}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 800, 
                fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" }, 
                color: primaryColor, 
                ml: { xs: 1, sm: 2 } 
              }}>
                {(() => {
                  const { price, currency } = getVariantPriceAndCurrency(variant, index);
                  return formatPrice(price, currency);
                })()}
              </Typography>
            </Box>

            <QuantityControls>
              <QuantityButton
                size="small"
                onClick={() => updateQuantity(index, -1)}
                disabled={quantity <= 0}
              >
                <RemoveIcon fontSize="small" />
              </QuantityButton>
              
              <QuantityDisplay>
                {quantity}
              </QuantityDisplay>
              
              <QuantityButton
                size="small"
                onClick={() => updateQuantity(index, 1)}
                disabled={isMaxStockReached}
              >
                <AddIcon fontSize="small" />
              </QuantityButton>

              {hasQuantity && (
                <CustomizeButton size="small" onClick={() => handleOpenCustomize(index)}>
                  <CustomizeIcon size="16" />
                  customize
                </CustomizeButton>
              )}
            </QuantityControls>
          </CardContent>
        </VariantCard>

        {/* Note Section for this variant */}
        {hasQuantity && (
          <Box sx={{ 
            mb: { xs: 4, sm: 5, md: 6 }, 
            padding: { xs: "0 16px", sm: "0 18px", md: "0 20px" } 
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.9rem" }, 
              color: "#1E293B", 
              mb: 1, 
              textTransform: "lowercase" 
            }}>
              note for {variant.name}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="please make it extra extra extra spicy"
              value={note}
              onChange={(e) => updateVariantNote(index, e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "#E2E8F0" },
                  "&:hover fieldset": { borderColor: primaryColor },
                  "&.Mui-focused fieldset": { borderColor: primaryColor },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.9rem",
                  textTransform: "lowercase",
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // Render single item (no variants)
  const renderSingleItem = () => {
    const quantity = selectedVariants['single'] || 0;
    const hasQuantity = quantity > 0;
    const note = variantNotes['single'] || "";
    const stockOnHand = getItemStockOnHand(itemData);
    const isStockLimited = stockOnHand !== null;
    const isMaxStockReached = isStockLimited && quantity >= stockOnHand;

    return (
      <Box sx={{ marginBottom: 2 }}>
        <VariantCard>
          <CardContent sx={{ 
            padding: { xs: "16px", sm: "18px", md: "20px" } 
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.1rem" }, 
                    color: "#1E293B", 
                    textTransform: "lowercase"
                  }}>
                    {itemData.name}
                  </Typography>
                  {/* Stock Icon with Popover for single item */}
                  {itemData?.showStockToCustomer && getSingleItemStockInformation() && (
                    <>
                      <Box
                        onMouseEnter={(e) => handleStockPopoverOpen(e, getSingleItemStockInformation())}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'color 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: "#E1E0DA",
                          p: 1,
                          borderRadius: 1,
                          '&:hover': {
                            '& svg path': {
                              fill: primaryColor
                            }
                          }
                        }}
                      >
                        <StockLogo 
                          size={14}
                          color="#000"
                        />
                      </Box>
                    </>
                  )}
                </Box>
                <Typography variant="body2" sx={{ 
                  color: "#64748B", 
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.9rem" }, 
                  textTransform: "lowercase", 
                  mb: 0.5 
                }}>
                  {itemData.description}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: "#94A3B8", 
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.8rem" }, 
                  fontWeight: 600 
                }}>
                  #{itemData.code || "01"}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 800, 
                fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" }, 
                color: primaryColor, 
                ml: { xs: 1, sm: 2 } 
              }}>
                {(() => {
                  const { price, currency } = getItemPriceAndCurrency(itemData);
                  return formatPrice(price, currency);
                })()}
              </Typography>
            </Box>

            <QuantityControls>
              <QuantityButton
                size="small"
                onClick={() => updateQuantity('single', -1)}
                disabled={quantity <= 0}
              >
                <RemoveIcon fontSize="small" />
              </QuantityButton>
              
              <QuantityDisplay>
                {quantity}
              </QuantityDisplay>
              
              <QuantityButton
                size="small"
                onClick={() => updateQuantity('single', 1)}
                disabled={isMaxStockReached}
              >
                <AddIcon fontSize="small" />
              </QuantityButton>

              {hasQuantity && (
                <CustomizeButton size="small" onClick={() => handleOpenCustomize('single')}>
                  <CustomizeIcon size="16" />
                  customize
                </CustomizeButton>
              )}
            </QuantityControls>
          </CardContent>
        </VariantCard>

        {/* Note Section for single item */}
        {hasQuantity && (
          <Box sx={{ 
            marginTop: { xs: 1.5, sm: 1.8, md: 2 }, 
            padding: { xs: "0 16px", sm: "0 18px", md: "0 20px" } 
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.9rem" }, 
              color: "#1E293B", 
              mb: 1, 
              textTransform: "lowercase" 
            }}>
              note
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="please make it extra extra extra spicy"
              value={note}
              onChange={(e) => updateVariantNote('single', e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "#E2E8F0" },
                  "&:hover fieldset": { borderColor: primaryColor },
                  "&.Mui-focused fieldset": { borderColor: primaryColor },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.9rem",
                  textTransform: "lowercase",
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // Render customize dialog
  const renderCustomizeDialog = () => {
    if (!showCustomizeDialog) return null;

    const variantData = getVariantData();
    const variantsForTabs = getVariantsForTabs();
    const totalItems = getTotalItems();
    const customizedCount = getCustomizedCount();

    return (
      <Box sx={{ 
        position: "absolute", 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: "white", 
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto"
      }}>
        {/* Customize Header */}
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={handleBackFromCustomize}
              sx={{
                color: primaryColor, backgroundColor: primaryLight,
                "&:hover": { backgroundColor: primaryColor, color: "white", transform: "scale(1.05)" },
                transition: commonTransition,
              }}
            >
              <BackIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", textTransform: "lowercase" }}>
              {showSubstitutionOptions ? "sub options" : "choose item to remove"}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseCustomize}
            sx={{
              color: primaryColor, backgroundColor: primaryLight,
              "&:hover": { backgroundColor: primaryColor, color: "white", transform: "scale(1.05)" },
              transition: commonTransition,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        <ContentContainer>
          {/* Loading Overlay for Customize Dialog */}
          {isCustomizeLoading && (
            <Box sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}>
              <CircularProgress 
                size={50} 
                sx={{ 
                  color: primaryColor,
                }} 
              />
            </Box>
          )}
          <ScrollableContent>
            {!showSubstitutionOptions ? (
              <>
                {/* Item Info with Image Layout */}
                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                  {/* Left Side - Item Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: "1.8rem", color: "#1E293B", textTransform: "lowercase" }}>
                      {variantData.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#64748B", fontSize: "1rem", textTransform: "lowercase", mb: 2 }}>
                      {variantData.description}
                    </Typography>
                    
                    {/* Note Section - Show actual note for this variant */}
                    {(() => {
                      // Get the note for this specific variant
                      let variantNote = "";
                      if (selectedVariantForCustomize) {
                        if (selectedVariantForCustomize.startsWith('single-')) {
                          variantNote = variantNotes['single'] || "";
                        } else {
                          const variantIndex = parseInt(selectedVariantForCustomize.split('-')[0]);
                          variantNote = variantNotes[variantIndex] || "";
                        }
                      }
                      
                      return variantNote ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <Box sx={{ 
                            width: 16, height: 16, backgroundColor: "#DC2626", borderRadius: 1,
                            display: "flex", alignItems: "center", justifyContent: "center"
                          }}>
                            <Typography sx={{ color: "white", fontSize: "0.7rem", fontWeight: "bold" }}>!</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: primaryColor, fontSize: "0.9rem", fontWeight: 600, textTransform: "lowercase" }}>
                            note: {variantNote}
                          </Typography>
                        </Box>
                      ) : null;
                    })()}
                    
                    {/* Summary */}
                    <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                      <Typography variant="body1" sx={{ color: "#1E293B", fontWeight: 600 }}>
                        total quantity: {totalItems}
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#1E293B", fontWeight: 600 }}>
                        customized: {customizedCount}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right Side - Item Image */}
                  <Box sx={{ width: 120, height: 120, borderRadius: 2, overflow: "hidden", flexShrink: 0 }}>
                    {itemData.imageVideo?.[0] ? (
                      <img
                        src={getImageUrl(itemData.imageVideo[0])}
                        alt={variantData.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: "100%", height: "100%", 
                        backgroundColor: "#F8FAFC", 
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "2rem"
                      }}>
                        🍽️
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Variant Tabs - Horizontally Scrollable */}
                {variantsForTabs.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      display: "flex", 
                      backgroundColor: "#E4E4E4", 
                      borderRadius: 8, 
                      padding: 1,
                      gap: 1,
                      overflowX: "auto",
                      "&::-webkit-scrollbar": { height: 4 },
                      "&::-webkit-scrollbar-track": { background: "#E4E4E4", borderRadius: 2 },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#CBD5E1", borderRadius: 8,
                        "&:hover": { background: "#94A3B8" },
                      },
                    }}>
                      {variantsForTabs.map((tab) => (
                        <VariantTab
                          key={tab.key}
                          className={selectedVariantForCustomize === tab.key ? "selected" : ""}
                          onClick={() => setSelectedVariantForCustomize(tab.key)}
                          sx={{ 
                            minWidth: 100,
                            flexShrink: 0,
                            backgroundColor: selectedVariantForCustomize === tab.key ? "#FDFDFD" : "transparent",
                            color: selectedVariantForCustomize === tab.key ? "#000000" : "#64748B",
                            border: "none",
                            borderRadius: 16,
                            position: "relative",
                            "&.selected": {
                              backgroundColor: "#FDFDFD",
                              color: "#000000",
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                            {tab.itemNumber}: {tab.name}
                            {addedSides[tab.key] && Object.keys(addedSides[tab.key]).length > 0 && (
                              <Chip
                                label="sides"
                                size="small"
                                sx={{
                                  ml: 1,
                                  height: 16, fontSize: "0.6rem", fontWeight: 600,
                                  backgroundColor: primaryColor, color: "white",
                                  border: "none", textTransform: "lowercase",
                                }}
                              />
                            )}
                          </Typography>
                          {/* Cross icon only for selected tab */}
                          {selectedVariantForCustomize === tab.key && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveVariant(tab.key);
                              }}
                              sx={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                width: 20, height: 20, 
                                backgroundColor: "#EFC1FF",
                                color: "#000",
                                "&:hover": {
                                  backgroundColor: "#DC2626",
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          )}
                        </VariantTab>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Add Side Button - Only show if item has sides and allows side addition */}
                {itemData.sides?.length > 0 && itemData.allowSideAddition && (
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={handleOpenAddSides}
                      sx={{
                        height: 40, borderRadius: 2, textTransform: "lowercase",
                        fontSize: "0.9rem", fontWeight: 600,
                        backgroundColor: primaryColor, color: "white",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#7C4DFF",
                          border: "none",
                        },
                      }}
                    >
                      add side
                    </Button>
                  </Box>
                )}

                {/* Added Sides Display - Only show for current selected item */}
                {selectedVariantForCustomize && addedSides[selectedVariantForCustomize] && Object.keys(addedSides[selectedVariantForCustomize]).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", mb: 2, textTransform: "lowercase" }}>
                      added sides for this item:
                    </Typography>
                    {Object.entries(addedSides[selectedVariantForCustomize]).map(([sideId, quantity]) => {
                      const side = getSideData(sideId);
                      if (!side) return null;
                      
                      return (
                        <Box key={sideId} sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between",
                          padding: "12px 16px", 
                          backgroundColor: "#F8FAFC", 
                          borderRadius: 8,
                          border: "1px solid #E2E8F0", 
                          marginBottom: 4,
                        }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ 
                              width: 40, height: 40, borderRadius: 1, overflow: "hidden",
                              backgroundColor: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                              {side.imageVideo?.[0] ? (
                                <img
                                  src={getImageUrl(side.imageVideo[0])}
                                  alt={side.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <Typography sx={{ fontSize: "1.2rem" }}>🍽️</Typography>
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: "#1E293B", textTransform: "lowercase" }}>
                                {getSideDisplayName(side)} x {quantity}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.8rem", textTransform: "lowercase" }}>
                                {side.description}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: primaryColor }}>
                              +{formatPrice(getSidePrice(side) * quantity, getSideCurrency(side))}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeAddedSide(sideId)}
                              sx={{
                                width: 24, height: 24,
                                backgroundColor: "#FECACA",
                                color: "#DC2626",
                                "&:hover": {
                                  backgroundColor: "#DC2626",
                                  color: "white",
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Variant Total Price Display - Only show if sides are added */}
                {(() => {
                  if (selectedVariantForCustomize) {
                    let sidesPrice;
                    
                    if (selectedVariantForCustomize.startsWith('single-')) {
                      sidesPrice = getTotalSidesPrice(selectedVariantForCustomize);
                    } else {
                      sidesPrice = getTotalSidesPrice(selectedVariantForCustomize);
                    }
                    
                    // Only render if sides are added (sidesPrice > 0)
                    if (sidesPrice > 0) {
                      let variantIndex, variantPrice, totalPrice, currency;
                      
                      if (selectedVariantForCustomize.startsWith('single-')) {
                        variantIndex = 'single';
                        variantPrice = getVariantPrice('single');
                        totalPrice = getVariantTotalPrice('single', selectedVariantForCustomize);
                        const { currency: itemCurrency } = getItemPriceAndCurrency(itemData);
                        currency = itemCurrency || itemData.currency;
                      } else {
                        variantIndex = parseInt(selectedVariantForCustomize.split('-')[0]);
                        variantPrice = getVariantPrice(variantIndex);
                        totalPrice = getVariantTotalPrice(variantIndex, selectedVariantForCustomize);
                        const variant = itemData.variants?.[variantIndex];
                        const { currency: variantCurrency } = getVariantPriceAndCurrency(variant, variantIndex);
                        currency = variantCurrency || itemData.currency;
                      }
                      
                      return (
                        <Box sx={{ 
                          padding: "16px", 
                          backgroundColor: "#F8FAFC", 
                          borderRadius: 2,
                          border: "1px solid #E2E8F0", 
                          marginBottom: 2,
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: "#1E293B", mb: 2, textTransform: "lowercase" }}>
                            variant total price:
                          </Typography>
                          
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                              <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.9rem", textTransform: "lowercase" }}>
                                variant price:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B", fontSize: "0.9rem" }}>
                                {formatPrice(variantPrice, currency)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                              <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.9rem", textTransform: "lowercase" }}>
                                sides price:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B", fontSize: "0.9rem" }}>
                                +{formatPrice(sidesPrice, currency)}
                              </Typography>
                            </Box>
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", textTransform: "lowercase" }}>
                                total:
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.2rem", color: primaryColor }}>
                                {formatPrice(totalPrice, currency)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  }
                  return null;
                })()}

                {/* Customizable Ingredients */}
                {(() => {
                  const customizableIngredients = variantData.ingredients.filter(ingredient => {
                    // Only show ingredients that have substitutes or are removable
                    const isRemovable = variantData.removableIngredients.includes(ingredient.name);
                    const hasSubstitutes = ingredient.substitutes && ingredient.substitutes.length > 0;
                    return isRemovable || hasSubstitutes;
                  });

                  // Don't render anything if no customizable ingredients
                  if (customizableIngredients.length === 0) {
                    return null;
                  }

                  return (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", mb: 2, textTransform: "lowercase" }}>
                        customizable ingredients:
                      </Typography>
                      
                      {customizableIngredients.map((ingredient, index) => {
                      const isRemovable = variantData.removableIngredients.includes(ingredient.name);
                      const hasSubstitutes = ingredient.substitutes && ingredient.substitutes.length > 0;
                      
                      // Get current customizations for this specific tab
                      const currentCustomizations = customizations[selectedVariantForCustomize] || {};
                      const isRemoved = currentCustomizations.removed?.[ingredient.name];
                      const substitution = currentCustomizations.substitutions?.[ingredient.name];
                      
                      return (
                        <IngredientItem key={ingredient._id || index}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: "#1E293B", textTransform: "lowercase" }}>
                              {isRemoved ? ingredient.name : (substitution || ingredient.name)}
                            </Typography>
                            {/* Show applied customizations */}
                            {isRemoved && (
                              <Typography variant="body2" sx={{ color: "#DC2626", fontSize: "0.8rem", fontStyle: "italic", textTransform: "lowercase" }}>
                                (removed)
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {hasSubstitutes && !isRemoved && (
                              <SubButton onClick={() => handleShowSubstitutionOptions(ingredient)}>
                                {substitution ? "change sub" : "sub"}
                              </SubButton>
                            )}
                            {isRemovable && !isRemoved && (
                              <RemoveButton onClick={() => handleRemoveIngredient(ingredient)}>
                                remove
                              </RemoveButton>
                            )}
                            {/* Show include button if ingredient is removed */}
                            {isRemoved && (
                              <SubButton
                                onClick={() => {
                                  const tabKey = selectedVariantForCustomize;
                                  setCustomizations(prev => ({
                                    ...prev,
                                    [tabKey]: {
                                      ...prev[tabKey],
                                      removed: {
                                        ...prev[tabKey]?.removed,
                                        [ingredient.name]: false
                                      }
                                    }
                                  }));
                                }}
                              >
                                include
                              </SubButton>
                            )}
                          </Box>
                        </IngredientItem>
                      );
                    })}
                    </Box>
                  );
                })()}
              </>
            ) : (
              /* Substitution Options */
              <Box>
                {/* Summary for Sub Options */}
                <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                  <Typography variant="body1" sx={{ color: "#1E293B", fontWeight: 600 }}>
                    total quantity: {totalItems}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#1E293B", fontWeight: 600 }}>
                    customized: {customizedCount}
                  </Typography>
                </Box>



                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", mb: 2, textTransform: "lowercase" }}>
                  sub "{selectedIngredientForSub?.name}" with one of the following options:
                </Typography>
                
                {/* Original ingredient option - only show if there's already a substitution */}
                {(() => {
                  const currentCustomizations = customizations[selectedVariantForCustomize] || {};
                  const currentSubstitution = currentCustomizations.substitutions?.[selectedIngredientForSub.name];
                  
                  // Only show original ingredient if there's already a substitution
                  if (currentSubstitution) {
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#1E293B", textTransform: "lowercase" }}>
                          {selectedIngredientForSub?.name}
                        </Typography>
                        <SubButton onClick={() => handleSubstituteIngredient(selectedIngredientForSub, selectedIngredientForSub.name)}>
                          sub
                        </SubButton>
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                {/* Substitute options - filter out currently selected substitute */}
                {selectedIngredientForSub?.substitutes?.filter(substitute => {
                  // Get current substitution for this ingredient
                  const currentCustomizations = customizations[selectedVariantForCustomize] || {};
                  const currentSubstitution = currentCustomizations.substitutions?.[selectedIngredientForSub.name];
                  // Don't show the currently selected substitute
                  return substitute !== currentSubstitution;
                }).map((substitute, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#1E293B", textTransform: "lowercase" }}>
                      {substitute}
                    </Typography>
                    <SubButton onClick={() => handleSubstituteIngredient(selectedIngredientForSub, substitute)}>
                      sub
                    </SubButton>
                  </Box>
                ))}
              </Box>
            )}
          </ScrollableContent>
          
          {/* Done Button - Only show when not in substitution options */}
          {!showSubstitutionOptions && (
            <Box sx={{ 
              display: "flex", 
              justifyContent: "center", 
              borderTop: "2px solid #E2E8F0", 
              marginTop: "auto",
              pt: "12px",
            }}>
              <Button
                onClick={handleCloseCustomize}
                sx={{
                  height: 40,
                  minWidth: 120,
                  borderRadius: 8,
                  textTransform: "lowercase",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
                  color: "white",
                }}
              >
                done
              </Button>
            </Box>
          )}
        </ContentContainer>
        
        {/* Resize Handle for Customize Dialog */}
        <ResizeHandle onMouseDown={handleResizeStart} />
      </Box>
    );
  };

  // Render add sides dialog
  const renderAddSidesDialog = () => {
    if (!showAddSidesDialog) return null;

    const totalSidesPrice = Object.entries(selectedSides).reduce((total, [sideId, quantity]) => {
      const side = getSideData(sideId);
      return total + getSidePrice(side) * quantity;
    }, 0);

    return (
      <Box sx={{ 
        position: "absolute", 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: "white", 
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto"
      }}>
        {/* Add Sides Header */}
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", textTransform: "lowercase" }}>
              add sides
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseAddSides}
            sx={{
              color: primaryColor, backgroundColor: primaryLight,
              "&:hover": { backgroundColor: primaryColor, color: "white", transform: "scale(1.05)" },
              transition: commonTransition,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        <ContentContainer>
          {/* Loading Overlay for Add Sides Dialog */}
          {isSidesLoading && (
            <Box sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}>
              <CircularProgress 
                size={50} 
                sx={{ 
                  color: primaryColor,
                }} 
              />
            </Box>
          )}
          <ScrollableContent>
            {/* Sides with this item */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", mb: 2, textTransform: "lowercase" }}>
                sides with this item:
              </Typography>
              
              {itemData.sides?.length > 0 && itemData.allowSideAddition ? itemData.sides.map((side) => {
                const quantity = selectedSides[side._id] || 0;
                
                return (
                  <Box key={side._id} sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between",
                    padding: "12px", 
                    backgroundColor: "white", 
                    borderRadius: 3,
                    border: "1px solid #E2E8F0", 
                    marginBottom: 5,
                    transition: commonTransition,
                    "&:hover": {
                      borderColor: primaryColor,
                      boxShadow: "0 2px 8px rgba(98, 0, 238, 0.1)",
                    },
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                      {/* Side Image */}
                      <Box sx={{ 
                        width: 60, height: 60, borderRadius: 2, overflow: "hidden",
                        backgroundColor: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0
                      }}>
                        {side.imageVideo?.[0] ? (
                          <img
                            src={getImageUrl(side.imageVideo[0])}
                            alt={side.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: "1.5rem" }}>🍽️</Typography>
                        )}
                      </Box>
                      
                      {/* Side Info */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1E293B", textTransform: "lowercase" }}>
                            {getSideDisplayName(side)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.9rem", textTransform: "lowercase", mb: 1 }}>
                          {side.description}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: primaryColor, fontSize: "1rem" }}>
                          {formatPrice(getSidePrice(side), getSideCurrency(side))}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Quantity Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2 }}>
                      <QuantityButton
                        size="small"
                        onClick={() => updateSideQuantity(side._id, -1)}
                        disabled={quantity <= 0}
                      >
                        <RemoveIcon fontSize="small" />
                      </QuantityButton>
                      
                      <QuantityDisplay>
                        {quantity}
                      </QuantityDisplay>
                      
                      <QuantityButton
                        size="small"
                        onClick={() => updateSideQuantity(side._id, 1)}
                      >
                        <AddIcon fontSize="small" />
                      </QuantityButton>
                    </Box>
                  </Box>
                );
              }) : (
                <Box sx={{ 
                  padding: "24px", 
                  textAlign: "center", 
                  backgroundColor: "#F8FAFC", 
                  borderRadius: 12,
                  border: "1px solid #E2E8F0"
                }}>
                  <Typography variant="body1" sx={{ color: "#64748B", fontSize: "0.9rem", textTransform: "lowercase" }}>
                    no sides available for this item
                  </Typography>
                </Box>
              )}
            </Box>
          </ScrollableContent>

          {/* Bottom Actions */}
          <BottomActions>
            <ActionButton 
              className="secondary"
              onClick={handleCloseAddSides}
            >
              cancel
            </ActionButton>
            <ActionButton 
              className="primary"
              onClick={handleAddSides}
              disabled={Object.keys(selectedSides).length === 0}
            >
              add {totalSidesPrice > 0 ? `(+${formatPrice(totalSidesPrice, itemData.currency)})` : ''}
            </ActionButton>
          </BottomActions>
        </ContentContainer>
        
        {/* Resize Handle for Add Sides Dialog */}
        <ResizeHandle onMouseDown={handleResizeStart} />
      </Box>
    );
  };

  if (!open || !itemData) return null;

  const imageUrl = getImageUrl(itemData.imageVideo?.[0]);
  const totalItems = getTotalItems();

  return (
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        {/* Loading Overlay */}
        {isLoading && (
          <Box sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
            <CircularProgress 
              size={60} 
              sx={{ 
                color: primaryColor,
              }} 
            />
          </Box>
        )}
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={handleBack}
              size={window.innerWidth < 600 ? "small" : "medium"}
              sx={{
                color: primaryColor, backgroundColor: primaryLight,
                "&:hover": { backgroundColor: primaryColor, color: "white", transform: "scale(1.05)" },
                transition: commonTransition,
              }}
            >
              <BackIcon fontSize={window.innerWidth < 600 ? "small" : "medium"} />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ 
                fontWeight: 800, 
                fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" }, 
                textTransform: "lowercase" 
              }}>
                {itemData.name}
              </Typography>
              {isDraftSaved && (
                <Chip
                  label="draft saved"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    backgroundColor: "#F0F9FF",
                    color: "#0369A1",
                    border: "1px solid #BAE6FD",
                    textTransform: "lowercase",
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{
              color: primaryColor, backgroundColor: primaryLight,
              "&:hover": { backgroundColor: primaryColor, color: "white", transform: "scale(1.05)" },
              transition: commonTransition,
            }}
          >
            <CloseIcon fontSize={window.innerWidth < 600 ? "small" : "medium"} />
          </IconButton>
        </DialogHeader>

        <ContentContainer>
          <ScrollableContent>
            {/* Item Image/Video Swiper with Flip Options */}
            <ImageSection>
              <SwiperContainer>
                {viewMode === 'image' ? (
                  // Image/Video Swiper
                  <Swiper
                    modules={[Pagination, Autoplay]}
                    pagination={{ clickable: true }}
                    autoplay={{
                      delay: 3000,
                      disableOnInteraction: false,
                    }}
                    spaceBetween={0}
                    slidesPerView={1}
                    loop={itemData.imageVideo?.length > 1}
                  >
                    {itemData.imageVideo?.map((imagePath, index) => {
                      const fullImageUrl = getImageUrl(imagePath);
                      return (
                        <SwiperSlide key={index}>
                          {fullImageUrl ? (
                            <ItemImage
                              src={fullImageUrl}
                              alt={`${itemData.name} - Image ${index + 1}`}
                            />
                          ) : (
                            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem" }}>
                              🍽️
                            </Box>
                          )}
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                ) : viewMode === 'qrcode' ? (
                  // QR Code View
                  <QRCodeContainer>
                    <QRCode
                      value={itemData._id || itemData.code || "default"}
                      size={window.innerWidth < 600 ? 80 : window.innerWidth < 960 ? 100 : 120}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    />
                  </QRCodeContainer>
                ) : (
                  // Barcode View
                  <BarcodeContainer>
                    <Barcode
                      value={itemData._id || itemData.code || "123456789012"}
                      format="CODE128"
                      width={window.innerWidth < 600 ? 1.2 : 1.5}
                      height={window.innerWidth < 600 ? 40 : window.innerWidth < 960 ? 50 : 60}
                      displayValue={true}
                      fontSize={window.innerWidth < 600 ? 10 : 12}
                      style={{ height: "100%", width: "100%" }}
                    />
                  </BarcodeContainer>
                )}
                
                {/* Flip Button */}
                <FlipButton onClick={handleFlip}>
                  {getFlipIcon()}
                </FlipButton>
              </SwiperContainer>
            </ImageSection>

            {/* Item Details Section */}
            <ItemDetailsSection>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 800, 
                  fontSize: { xs: "1.4rem", sm: "1.6rem", md: "1.8rem" }, 
                  color: "#1E293B", 
                  textTransform: "lowercase",
                }}>
                  {itemData.name}
                </Typography>
                {/* Info Icon with Popover */}
                <Box
                  onMouseEnter={handleInfoPopoverOpen}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: "#E1E0DA",
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      '& svg path': {
                        fill: primaryColor
                      }
                    }
                  }}
                >
                  <InfoIcon 
                    size={window.innerWidth < 600 ? 14 : window.innerWidth < 960 ? 16 : 18}
                    color="#64748B"
                  />
                </Box>
                
                {/* Popover */}
                <Popover
                  open={Boolean(infoPopoverAnchor)}
                  anchorEl={infoPopoverAnchor}
                  onClose={handleInfoPopoverClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  disableRestoreFocus
                  sx={{
                    zIndex: 10001, // Higher than dialog z-index (10000)
                    '& .MuiPopover-paper': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid #E2E8F0',
                      padding: { xs: 2, sm: 2.5, md: 3 },
                      minWidth: { xs: 240, sm: 260, md: 280 },
                      maxWidth: { xs: '90vw', sm: 350, md: 400 },
                      mt: 1,
                      zIndex: 10001,
                      // Adjust positioning for xs screens
                      '&': {
                        [theme.breakpoints.down('sm')]: {
                          left: '50% !important',
                          transform: 'translateX(-50%) !important',
                          right: 'auto !important',
                        }
                      }
                    }
                  }}
                >
                  {/* All Ingredients Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      fontSize: "1rem", 
                      color: "#1E293B", 
                      mb: 2,
                      textTransform: "lowercase"
                    }}>
                      all ingredients
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {itemData.ingredients?.map((ingredient, index) => (
                        <Box key={ingredient._id || index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            borderRadius: '50%', 
                            backgroundColor: primaryColor,
                            flexShrink: 0
                          }} />
                          <Typography variant="body2" sx={{ 
                            color: "#64748B", 
                            fontSize: "0.85rem",
                            textTransform: "lowercase"
                          }}>
                            {ingredient.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Nutritional Info Section */}
                  {(() => {
                    const nutrition = getNutritionData();
                    if (!nutrition) return null;

                    const nutritionData = [
                      { label: "total weight", value: nutrition.weight, unit: "g" },
                      { label: "energy", value: nutrition.kcal, unit: " kcal" },
                      { label: "protein", value: nutrition.protein, unit: "g" },
                      { label: "carbs", value: nutrition.carbohydrates, unit: "g" },
                      { label: "fat", value: nutrition.fats, unit: "g" },
                      { label: "fibre", value: nutrition.fibre, unit: "g" }
                    ];

                    return (
                      <Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          fontSize: "1rem", 
                          color: "#1E293B", 
                          mb: 2,
                          textTransform: "lowercase"
                        }}>
                          nutritional info
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
                          {nutritionData.map((item, index) => (
                            <NutritionRow 
                              key={index}
                              label={item.label}
                              value={item.value}
                              unit={item.unit}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                </Popover>
                
                {/* Stock Popover - Shared for variants and single items */}
                <Popover
                  open={Boolean(stockPopoverAnchor) && stockPopoverData !== null}
                  anchorEl={stockPopoverAnchor}
                  onClose={handleStockPopoverClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  disableRestoreFocus
                  sx={{
                    zIndex: 10001,
                    '& .MuiPopover-paper': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid #E2E8F0',
                      padding: { xs: 2, sm: 2.5, md: 3 },
                      minWidth: { xs: 240, sm: 260, md: 280 },
                      maxWidth: { xs: '90vw', sm: 350, md: 400 },
                      mt: 1,
                      zIndex: 10001,
                      '&': {
                        [theme.breakpoints.down('sm')]: {
                          left: '50% !important',
                          transform: 'translateX(-50%) !important',
                          right: 'auto !important',
                        }
                      }
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    fontSize: "1rem", 
                    color: "#1E293B", 
                    mb: 2,
                    textTransform: "lowercase"
                  }}>
                    stock information
                  </Typography>
                  
                  {stockPopoverData && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          color: "#64748B", 
                          fontSize: "0.85rem",
                          textTransform: "lowercase"
                        }}>
                          stock on hand (qty):
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: "#1E293B", 
                          fontSize: "0.85rem"
                        }}>
                          {stockPopoverData.stockOnHand}
                        </Typography>
                      </Box>
                      
                      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          color: "#64748B", 
                          fontSize: "0.85rem",
                          textTransform: "lowercase"
                        }}>
                          stock committed:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: "#1E293B", 
                          fontSize: "0.85rem"
                        }}>
                          {stockPopoverData.stockCommitted}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          color: "#64748B", 
                          fontSize: "0.85rem",
                          textTransform: "lowercase"
                        }}>
                          awaiting new stock delivery:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: "#1E293B", 
                          fontSize: "0.85rem"
                        }}>
                          {stockPopoverData.awaitingNewStockDelivery || "n/a"}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ 
                          color: "#64748B", 
                          fontSize: "0.85rem",
                          textTransform: "lowercase"
                        }}>
                          awaiting new stock count (qty):
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: "#1E293B", 
                          fontSize: "0.85rem"
                        }}>
                          {stockPopoverData.awaitingNewStockCount}
                        </Typography>
                      </Box> */}
                    </Box>
                  )}
                </Popover>
              </Box>
              <Typography variant="body1" sx={{ 
                color: "#1C6C00",
                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }, 
                textTransform: "lowercase", 
                fontWeight: 500,
                marginRight: 2
              }}>
                {itemData.foodType?.name}
              </Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 }, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748B" }}>
                  <TimeIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }, 
                    textTransform: "lowercase", 
                    fontWeight: 500 
                  }}>
                    {formatTime(itemData.prepTime)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748B" }}>
                  <TimeIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }, 
                    textTransform: "lowercase", 
                    fontWeight: 500 
                  }}>
                    {formatTime(itemData.waitTime)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {itemData.dietTypes?.slice(0, 3).map((diet, index) => (
                  <Chip
                    key={diet._id || index}
                    label={diet.name}
                    size="small"
                    sx={{
                      height: { xs: 24, sm: 26, md: 28 }, 
                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" }, 
                      fontWeight: 600,
                      backgroundColor: "#F0F9FF", color: "#0369A1",
                      border: "1px solid #BAE6FD", textTransform: "lowercase",
                    }}
                  />
                ))}
              </Box>
              
             
              
            </ItemDetailsSection>
            {/* Variants or Single Item */}
            {itemData.variantEnabled && itemData.variants?.length > 0 ? (
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" }, 
                  color: "#1E293B", 
                  my: 2, 
                  textTransform: "lowercase" 
                }}>
                  select size
                </Typography>
                {itemData.variants
                  .map((variant, index) => ({ variant, index }))
                  .filter(({ variant }) => hasVariantStockForLocation(variant))
                  .map(({ variant, index }) => renderVariantCard(variant, index))}
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" }, 
                  color: "#1E293B", 
                  mb: 2, 
                  textTransform: "lowercase" 
                }}>
                  quantity
                </Typography>
                {renderSingleItem()}
              </Box>
            )}


          </ScrollableContent>

          {/* Bottom Actions */}
          <BottomActions>
            <ActionButton 
              className="primary"
              onClick={async () => {
                try {
                  // Check if total quantity is 0 and this is an existing cart item
                  if (totalItems === 0 && cartItemData && conversationId) {
                    // Delete the item from cart
                    await deleteCartItem(conversationId, itemData._id, businessLocationId);
                    toast.success("item removed from cart  ");
                    
                    // Clear all state
                    setSelectedVariants({});
                    setVariantNotes({});
                    setCustomizations({});
                    setAddedSides({});
                    
                    // Go back to menu dialog
                    onBack();
                  } else {
                    // Generate cart payload
                    const cartPayload = generateCartPayload();
                    
                    // Send to API
                    await addToCart(cartPayload, conversationId, businessLocationId);
                    toast.success(cartItemData ? "item updated in cart  " : "item added to cart  ");
                    
                    // Go back to menu dialog with selected items
                    onBack();
                  }
                } catch (error) {
                  toast.error(cartItemData ? "failed to update item in cart" : "failed to add item to cart");
                  
                }
              }}
              disabled={totalItems === 0 && !cartItemData}
            >
              {totalItems === 0 && cartItemData ? "remove item" : (cartItemData ? `update item (${totalItems})` : `add item (${totalItems})`)}
            </ActionButton>
          </BottomActions>
        </ContentContainer>

        <ResizeHandle onMouseDown={handleResizeStart} />
        
        {/* Customize Dialog Overlay */}
        {renderCustomizeDialog()}
        
        {/* Add Sides Dialog Overlay */}
        {renderAddSidesDialog()}
      </CustomDialog>
    </CustomOverlay>
  );
};

export default ItemOrderDetailDialog;
